import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  ClipboardList, 
  Calendar, 
  BookOpen, 
  GraduationCap,
  AlertTriangle,
  Clock,
  User,
  FileText,
  Play,
  ChevronRight,
  AlertCircle,
  CheckCircle2
} from "lucide-react";
import { format, parseISO, isPast, isToday, differenceInDays } from "date-fns";
import { useUserAssignments, UserAssignment } from "@/hooks/useUserAssignments";
import { useCompleteAssignment } from "@/hooks/useCompleteAssignment";
import { Skeleton } from "@/components/ui/skeleton";

type FilterType = "all" | "required" | "with_due_date" | "overdue";
type SortType = "due_date" | "assigned_date" | "urgency";

interface DueDateStatus {
  text: string;
  status: 'overdue' | 'urgent' | 'warning' | 'normal';
  daysUntilDue: number;
  isOverdue: boolean;
  isDueToday: boolean;
  isNearDue: boolean;
  displayText: string;
}

function getDueDateStatus(dueDate: string | null): DueDateStatus | null {
  if (!dueDate) return null;
  
  const now = new Date();
  const date = parseISO(dueDate);
  const daysUntilDue = differenceInDays(date, now);
  const isOverdue = isPast(date) && !isToday(date);
  const isDueToday = isToday(date);
  
  let status: DueDateStatus['status'];
  let displayText: string;
  
  if (isOverdue) {
    const daysOverdue = Math.abs(daysUntilDue);
    status = 'overdue';
    displayText = daysOverdue === 1 ? 'Overdue by 1 day' : `Overdue by ${daysOverdue} days`;
  } else if (isDueToday) {
    status = 'urgent';
    displayText = 'Due today!';
  } else if (daysUntilDue <= 3) {
    status = 'urgent';
    displayText = daysUntilDue === 1 ? 'Due in 1 day' : `Due in ${daysUntilDue} days`;
  } else if (daysUntilDue <= 7) {
    status = 'warning';
    displayText = `Due in ${daysUntilDue} days`;
  } else {
    status = 'normal';
    displayText = format(date, "MMM d, yyyy");
  }
  
  return {
    text: format(date, "MMM d, yyyy"),
    status,
    daysUntilDue,
    isOverdue,
    isDueToday,
    isNearDue: daysUntilDue <= 3 && daysUntilDue >= 0,
    displayText,
  };
}

function getStatusBadgeProps(dueDateStatus: DueDateStatus | null) {
  if (!dueDateStatus) {
    return { label: "Not Started", variant: "secondary" as const, className: "" };
  }
  
  switch (dueDateStatus.status) {
    case 'overdue':
      return { 
        label: "Overdue", 
        variant: "destructive" as const, 
        className: "" 
      };
    case 'urgent':
      return { 
        label: dueDateStatus.isDueToday ? "Due Today" : "Due Soon", 
        variant: "outline" as const, 
        className: "border-destructive text-destructive bg-destructive/10" 
      };
    case 'warning':
      return { 
        label: "Due Soon", 
        variant: "outline" as const, 
        className: "border-warning text-warning bg-warning/10" 
      };
    default:
      return { label: "Not Started", variant: "secondary" as const, className: "" };
  }
}

function getUrgencyScore(assignment: UserAssignment): number {
  const dueDateStatus = getDueDateStatus(assignment.due_date);
  let score = 0;
  
  // Overdue items are most urgent
  if (dueDateStatus?.isOverdue) {
    score = 1000 + Math.abs(dueDateStatus.daysUntilDue);
  } else if (dueDateStatus) {
    // Items due sooner are more urgent (invert days so smaller = higher score)
    score = 100 - Math.min(dueDateStatus.daysUntilDue, 100);
  }
  
  // Required items get a boost
  if (assignment.required) {
    score += 50;
  }
  
  return score;
}

export function MyAssignmentsTab() {
  const navigate = useNavigate();
  const { assignments, isLoading, count, error, refetch } = useUserAssignments();
  const completeAssignment = useCompleteAssignment();
  const [filter, setFilter] = useState<FilterType>("all");
  const [confirmingComplete, setConfirmingComplete] = useState<UserAssignment | null>(null);
  const [sort, setSort] = useState<SortType>("urgency");

  // Calculate stats
  const stats = useMemo(() => {
    let overdueCount = 0;
    let dueSoonCount = 0;
    
    assignments.forEach(a => {
      const status = getDueDateStatus(a.due_date);
      if (status?.isOverdue) overdueCount++;
      else if (status?.status === 'urgent' || status?.status === 'warning') dueSoonCount++;
    });
    
    return { overdueCount, dueSoonCount };
  }, [assignments]);

  const filteredAndSortedAssignments = useMemo(() => {
    let result = [...assignments];

    // Apply filter
    switch (filter) {
      case "required":
        result = result.filter(a => a.required);
        break;
      case "with_due_date":
        result = result.filter(a => a.due_date);
        break;
      case "overdue":
        result = result.filter(a => {
          const status = getDueDateStatus(a.due_date);
          return status?.isOverdue || status?.status === 'urgent';
        });
        break;
    }

    // Apply sort
    result.sort((a, b) => {
      if (sort === "urgency") {
        // Sort by urgency score (higher = more urgent)
        const scoreA = getUrgencyScore(a);
        const scoreB = getUrgencyScore(b);
        if (scoreA !== scoreB) return scoreB - scoreA;
        // Fallback to assigned date
        return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
      } else if (sort === "due_date") {
        // Required first, then by due date (soonest first, nulls last)
        if (a.required !== b.required) return a.required ? -1 : 1;
        if (a.due_date !== b.due_date) {
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        }
        return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
      } else {
        // By assigned date (newest first)
        return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
      }
    });

    return result;
  }, [assignments, filter, sort]);

  const handleNavigate = (assignment: UserAssignment) => {
    if (assignment.type === 'curriculum') {
      navigate(`/curricula/${assignment.item_id}`);
    } else {
      navigate(`/courses/${assignment.item_id}`);
    }
  };

  const handleMarkComplete = (assignment: UserAssignment) => {
    setConfirmingComplete(assignment);
  };

  const confirmComplete = () => {
    if (confirmingComplete) {
      completeAssignment.mutate({
        id: confirmingComplete.id,
        type: confirmingComplete.type,
      });
      setConfirmingComplete(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <Skeleton className="h-10 w-40" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardContent className="py-16 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Failed to load assignments
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto mb-4">
            Something went wrong while fetching your assignments. Please try again.
          </p>
          <Button onClick={() => refetch()} variant="outline">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (count === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <ClipboardList className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            No assignments yet
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            When your administrator assigns learning content, it will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Warning banner for overdue items */}
      {stats.overdueCount > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
            <p className="text-sm font-medium text-destructive">
              You have {stats.overdueCount} overdue assignment{stats.overdueCount !== 1 ? 's' : ''}
              {stats.dueSoonCount > 0 && ` and ${stats.dueSoonCount} due soon`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Header with count and filters */}
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{filteredAndSortedAssignments.length}</span>
          {filteredAndSortedAssignments.length !== count && (
            <span> of {count}</span>
          )}
          {" "}assignment{filteredAndSortedAssignments.length !== 1 ? 's' : ''}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="required">Required only</SelectItem>
              <SelectItem value="with_due_date">With due date</SelectItem>
              <SelectItem value="overdue">
                Overdue / Urgent
                {stats.overdueCount > 0 && ` (${stats.overdueCount})`}
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={sort} onValueChange={(v) => setSort(v as SortType)}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="urgency">Most urgent</SelectItem>
              <SelectItem value="due_date">By due date</SelectItem>
              <SelectItem value="assigned_date">By assigned date</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Assignments grid */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {filteredAndSortedAssignments.map((assignment) => {
          const dueDateStatus = getDueDateStatus(assignment.due_date);
          const statusBadge = getStatusBadgeProps(dueDateStatus);
          
          // Determine card border color based on urgency
          let cardBorderClass = "";
          if (dueDateStatus?.isOverdue) {
            cardBorderClass = "border-l-4 border-l-destructive";
          } else if (dueDateStatus?.status === 'urgent') {
            cardBorderClass = "border-l-4 border-l-destructive";
          } else if (dueDateStatus?.status === 'warning') {
            cardBorderClass = "border-l-4 border-l-warning";
          }
          
          return (
            <Card 
              key={assignment.id} 
              className={`flex flex-col hover:shadow-md transition-shadow ${cardBorderClass}`}
            >
              <CardContent className="flex flex-col flex-1 p-3 sm:p-4">
                {/* Header with type icon and badges */}
                <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10">
                      {assignment.type === 'curriculum' ? (
                        <GraduationCap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                      ) : (
                        <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] sm:text-xs capitalize">
                      {assignment.type}
                    </Badge>
                  </div>
                  
                  <div className="flex gap-1 sm:gap-1.5 flex-wrap justify-end">
                    {assignment.required && (
                      <Badge variant="secondary" className="text-[10px] sm:text-xs">
                        Required
                      </Badge>
                    )}
                    <Badge 
                      variant={statusBadge.variant}
                      className={`text-[10px] sm:text-xs ${statusBadge.className}`}
                    >
                      {statusBadge.label}
                    </Badge>
                  </div>
                </div>

                {/* Title */}
                <h3 className="font-semibold text-sm sm:text-base mb-2 sm:mb-3 line-clamp-2">
                  {assignment.item_title}
                </h3>

                {/* Meta info - hide some on mobile */}
                <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  {/* Assigned by - hidden on mobile */}
                  <div className="hidden sm:flex items-center gap-2">
                    <User className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">
                      Assigned by {assignment.assigned_by_name}
                    </span>
                  </div>
                  
                  {/* Assigned date - hidden on mobile */}
                  <div className="hidden sm:flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>
                      {format(parseISO(assignment.assigned_at), "MMM d, yyyy")}
                    </span>
                  </div>

                  {/* Due date with enhanced display */}
                  {dueDateStatus && (
                    <div 
                      className={`flex items-center gap-1.5 sm:gap-2 font-medium ${
                        dueDateStatus.status === 'overdue' 
                          ? 'text-destructive' 
                          : dueDateStatus.status === 'urgent'
                            ? 'text-destructive'
                            : dueDateStatus.status === 'warning'
                              ? 'text-warning'
                              : 'text-muted-foreground font-normal'
                      }`}
                    >
                      {dueDateStatus.status !== 'normal' ? (
                        <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                      ) : (
                        <Calendar className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                      )}
                      <span>{dueDateStatus.displayText}</span>
                    </div>
                  )}

                  {/* Notes - hidden on mobile */}
                  {assignment.notes && (
                    <div className="hidden sm:flex items-start gap-2">
                      <FileText className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-2 italic">
                        "{assignment.notes}"
                      </span>
                    </div>
                  )}
                </div>

                {/* Spacer to push button to bottom */}
                <div className="flex-1" />

                {/* Action buttons */}
                <div className="flex gap-2 mt-2">
                  <Button 
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    variant={dueDateStatus?.isOverdue ? "destructive" : "default"}
                    onClick={() => handleNavigate(assignment)}
                  >
                    <Play className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                    <span className="sm:hidden">Start</span>
                    <span className="hidden sm:inline">Start Learning</span>
                  </Button>
                  <Button 
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"
                    onClick={() => handleMarkComplete(assignment)}
                    title="Mark as Complete"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* No results after filtering */}
      {filteredAndSortedAssignments.length === 0 && count > 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              No assignments match the current filter.
            </p>
            <Button 
              variant="link" 
              onClick={() => setFilter("all")}
              className="mt-2"
            >
              Clear filter
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirmingComplete} onOpenChange={() => setConfirmingComplete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Assignment as Complete?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark "{confirmingComplete?.item_title}" as complete? 
              This will remove it from your active assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmComplete}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Mark Complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
